import HourglassTop from "@mui/icons-material/HourglassTop";
import { Alert, Typography } from "@mui/material";
import { SubmissionStepType } from "generated-graphql/graphql";
import { computeSubmissionReviewStates } from "hooks/submission/reviewStates";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useMemo } from "react";

import { pickDocumentStepStatusBanner } from "./documentStepBanner";

/**
 * Status banner shared by the document-form steps. The turn-taking (sequential
 * order, signer waiting on reviewers) comes from `computeSubmissionReviewStates`,
 * the same derivation the review surfaces use; the copy and the status→banner
 * decision live in `documentStepBanner.ts`.
 */
export function DocumentStepStatusBanner({ step }: { step: SubmissionStepType }) {
  const { isStaff, user } = useCurrentUser();
  const { submission } = useWasteStepper();

  const { reviewState } = useMemo(
    () => computeSubmissionReviewStates({ currentUser: user, submissionData: { submission } }),
    [user, submission]
  );

  const banner = pickDocumentStepStatusBanner({
    status: submission?.status,
    reviewState,
    isStaff,
    step,
    notReportingReason: submission?.notReportingReason,
    notReportingReasonNote: submission?.notReportingReasonNote,
  });
  if (!banner) {
    return null;
  }

  return (
    <Alert
      severity={banner.severity}
      icon={banner.severity === "warning" ? <HourglassTop /> : undefined}
    >
      {banner.title && (
        <Typography fontWeight="bold" marginBottom={0.5}>
          {banner.title}
        </Typography>
      )}
      <Typography variant="body2">{banner.message}</Typography>
    </Alert>
  );
}
